import {
  Button,
  mergeClasses,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add16Regular,
  Checkmark16Regular,
  ChevronUpDown16Regular,
  Open16Regular,
  Share16Regular,
} from "@fluentui/react-icons";
import {
  createConversation,
  getConversations,
  getMessagesOfConversation,
  getMyWorkspaces,
  trackConversationClick,
  trackConversationCopy,
} from "api";
import { ConstantValues } from "common/constants";
import { CopyOptionsDialog } from "components/conversations/copyOptionsDialog/CopyOptionsDialog";
import { ICopyResult } from "components/conversations/copyOptionsDialog/CopyOptionsDialog.types";
import { ConversationIcon } from "icons/ConversationIcon";
import { DocumentCopyIcon } from "icons/DocumentCopyIcon";
import { WorkspaceIcon } from "icons/WorkspaceIcon";
import { CopyOption } from "models/enums/CopyType";
import { LLMType } from "models/enums/LLMType";
import { IConversation } from "models/IConversation";
import { ICopyOption } from "models/ICopyOption";
import { IFile } from "models/IFile";
import { IMessage } from "models/IMessage";
import { ITag } from "models/ITag";
import { IUser } from "models/IUser";
import { IWorkspace } from "models/IWorkspace";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "store";
import { useChatStateReset } from "hooks/useChatStateReset";
import { isNullUndefinedOrEmpty } from "utils/generalUtils";
import { setActiveWorkspace } from "../../../redux/activeWorkspaceSlice";
import { setCurrentConversation } from "../../../redux/conversations";
import { setNewConfigForKey } from "../../../redux/llmConfig";
import { setCurrentMessages } from "../../../redux/messages";
import { clearReferencedConversations } from "../../../redux/referencedConversationsSlice";
import { setCurrentPrompt } from "../../../redux/selectedPrompt";
import { setTagsForKey } from "../../../redux/tags";
import { showToast } from "../../../redux/toastActions";
import { setLibraryDocumentsForKey } from "../../../redux/workspaceLibraryDocuments";
import {
  selectAvailableWorkspaces,
  setWorkspaces,
} from "../../../redux/workspacesSlice";
import { IWorkspaceLibraryDocument } from "../../manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { useSharedButtonStyles } from "theme/sharedStyles";
import { useTopBarStyles } from "./TopBar.styles";
import { ITopBarProps } from "./TopBar.types";

const OverflowTooltip: React.FC<{
  content: string;
  className?: string;
  children: React.ReactNode;
}> = ({ content, className, children }) => {
  const ref = useRef<HTMLDivElement>(null);
  const styles = useTopBarStyles();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const handleScrollOrInteraction = () => {
      setVisible(false);
    };

    window.addEventListener("scroll", handleScrollOrInteraction, true);
    window.addEventListener("wheel", handleScrollOrInteraction, true);

    return () => {
      window.removeEventListener("scroll", handleScrollOrInteraction, true);
      window.removeEventListener("wheel", handleScrollOrInteraction, true);
    };
  }, [visible]);

  return (
    <Tooltip
      content={<span className={styles.tooltipContent}>{content}</span>}
      appearance="inverted"
      relationship="description"
      positioning="after"
      visible={visible}
      onVisibleChange={(_, data) => {
        if (data.visible && ref.current) {
          const el =
            (ref.current.firstElementChild as HTMLElement) || ref.current;
          setVisible(el.scrollWidth > el.clientWidth);
        } else {
          setVisible(false);
        }
      }}
    >
      <div ref={ref} className={className}>
        {children}
      </div>
    </Tooltip>
  );
};

const NEW_CHAT: string = "New Chat";
const SELECT_WORKSPACE: string = "Select Workspace";

export const TopBar = (props: ITopBarProps): JSX.Element => {
  const { onManageContentClick } = props;
  const styles = useTopBarStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const resetChatState = useChatStateReset();
  const [searchParams] = useSearchParams();
  const hasUrlConversation = !!searchParams.get("conversationId");

  const [isLoadingWorkspaces, setIsLoadingWorkspaces] =
    useState<boolean>(false);
  const [workspacePopoverOpen, setWorkspacePopoverOpen] =
    useState<boolean>(false);
  const [conversationPopoverOpen, setConversationPopoverOpen] =
    useState<boolean>(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState<boolean>(false);
  const shareButtonRef = useRef<HTMLButtonElement>(null);

  const workspaces: IWorkspace[] = useSelector(selectAvailableWorkspaces);
  const activeWorkspace: IWorkspace | null = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const currentConversation: IConversation | null = useSelector(
    (state: RootState) => state.currentConversationSetter.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const users: IUser[] = useSelector(
    (state: RootState) => state.usersSlice.value,
  );
  const currentLLMConfig = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[ConstantValues.CHAT] ?? {
        llmType: LLMType.CLAUDE,
        rag: false,
        citate: false,
      },
  );
  const isGenerating = useSelector(
    (state: RootState) => state.aiGeneratedMessageSlice.isGenerating,
  );

  const workspaceId = activeWorkspace?.id ?? ConstantValues.EMPTY_STRING;

  useEffect((): void => {
    const fetchWorkspaces = async () => {
      setIsLoadingWorkspaces(true);
      const data: IWorkspace[] = await getMyWorkspaces();
      dispatch(setWorkspaces(data));
      setIsLoadingWorkspaces(false);
    };

    if (workspaces.length === 0) {
      fetchWorkspaces();
    }
  }, [dispatch, workspaces.length]);

  const { data: conversations = [], isLoading: isLoadingConversations } =
    useQuery(
      ["topBarConversations", workspaceId],
      async () => {
        if (!workspaceId) return [];
        return await getConversations({
          workspaceId,
          searchText: undefined,
          ascending: false,
          limit: 10,
          offset: 0,
        });
      },
      {
        enabled: Boolean(workspaceId),
      },
    );

  const displayTitle =
    currentConversation?.title ??
    conversations.find((c: IConversation) => c.id === currentConversation?.id)
      ?.title ??
    NEW_CHAT;

  useEffect(() => {
    if (!currentConversation?.id || !currentConversation?.title) return;
    queryClient.setQueryData<IConversation[]>(
      ["topBarConversations", workspaceId],
      (old) => {
        if (!old) return old ?? [];
        return old.map((c) =>
          c.id === currentConversation.id
            ? { ...c, title: currentConversation.title }
            : c,
        );
      },
    );
  }, [
    currentConversation?.id,
    currentConversation?.title,
    queryClient,
    workspaceId,
  ]);

  useEffect((): void => {
    if (hasUrlConversation) return;
    if (!activeWorkspace && workspaces.length > 0) {
      const storedWorkspaceId: string | null =
        sessionStorage.getItem("workspaceId");
      if (storedWorkspaceId) {
        const workspace: IWorkspace | undefined = workspaces.find(
          (w) => w.id === storedWorkspaceId,
        );
        if (workspace) {
          dispatch(setActiveWorkspace(workspace));
          sessionStorage.removeItem("workspaceId");

          const draftMessageWorkspaceId = localStorage.getItem(
            ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
          );
          if (
            draftMessageWorkspaceId &&
            draftMessageWorkspaceId !== workspace.id
          ) {
            localStorage.removeItem(
              ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
            );
          }
          return;
        }
      }

      const draftMessageWorkspaceId = localStorage.getItem(
        ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
      );
      if (draftMessageWorkspaceId) {
        const savedWorkspace: IWorkspace | undefined = workspaces.find(
          (w) => w.id === draftMessageWorkspaceId,
        );
        if (savedWorkspace) {
          dispatch(setActiveWorkspace(savedWorkspace));
          return;
        }
      }

      const personalWorkspace: IWorkspace | undefined = workspaces.find(
        (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
      );
      if (personalWorkspace) {
        dispatch(setActiveWorkspace(personalWorkspace));
        localStorage.setItem(
          ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
          personalWorkspace.id,
        );
      }
    }
  }, [activeWorkspace, workspaces, dispatch, hasUrlConversation]);

  useEffect(() => {
    // Skip localStorage recovery when URL params are present — ChatContentPage handles that
    if (hasUrlConversation) return;
    const draftMessageWorkspaceId: string | null = localStorage.getItem(
      ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
    );
    if (draftMessageWorkspaceId && workspaces.length > 0) {
      const workspace: IWorkspace | undefined = workspaces.find(
        (w) => w.id === draftMessageWorkspaceId,
      );
      if (workspace) {
        dispatch(setActiveWorkspace(workspace));
      }
    }
  }, [workspaces, dispatch, hasUrlConversation]);

  const currentTags: ITag[] = useSelector(
    (state: RootState) =>
      state.currentTagsSlice.tags[
        currentConversation?.id || ConstantValues.CHAT
      ] ?? [],
  );
  const currentLibraryDocuments: IWorkspaceLibraryDocument[] = useSelector(
    (state: RootState) =>
      state.workspaceLibraryDocumentsSlice.documents[
        currentConversation?.id || ConstantValues.CHAT
      ] ?? [],
  );
  const referencedConversations: IConversation[] = useSelector(
    (state: RootState) =>
      state.referencedConversationsSlice.conversations || [],
  );
  const currentMessages: IMessage[] = useSelector(
    (state: RootState) => state.currentMessagesSetter.value,
  );

  const uploadedDocuments: IFile[] = useMemo(() => {
    return currentMessages
      .flatMap((message) => message.files || [])
      .filter(
        (file, index, self) =>
          index === self.findIndex((f) => f.filename === file.filename),
      );
  }, [currentMessages]);

  const contentCount =
    currentTags.length +
    currentLibraryDocuments.length +
    referencedConversations.length +
    uploadedDocuments.length;

  const sortedWorkspaces: IWorkspace[] = useMemo(() => {
    const personalWorkspace: IWorkspace | undefined = workspaces.find(
      (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
    );
    const otherWorkspaces: IWorkspace[] = workspaces
      .filter((w) => w.name !== ConstantValues.PERSONAL_WORKSPACE)
      .sort((a, b) => a.name.localeCompare(b.name));

    return personalWorkspace
      ? [personalWorkspace, ...otherWorkspaces]
      : otherWorkspaces;
  }, [workspaces]);

  const handleCopy = async (
    conversation: IConversation,
    copyOption: ICopyOption,
  ): Promise<ICopyResult[]> => {
    try {
      let messages: IMessage[] = [];
      try {
        messages = await getMessagesOfConversation({
          conversationId: conversation.id,
        });
      } catch (error) {
        console.error("Failed to fetch messages for copy:", error);
      }

      const inputMessages = [...messages]
        .sort((a, b) => a.createdAt - b.createdAt)
        .map((message: IMessage) => ({
          content: message?.content,
          role: message?.role,
          tags: message?.tags,
          files: message?.files,
          sourceDocuments: message?.sourceDocuments,
          llmConfig: message?.llmConfig,
          userId: message?.userId,
          displayName: message?.displayName,
          createdAt: message?.createdAt ? message.createdAt : undefined,
        }));

      const resolvedTitle =
        conversation.title ||
        conversations.find((c: IConversation) => c.id === conversation.id)
          ?.title ||
        "Untitled";

      const baseConversation = {
        messages: inputMessages,
        title: resolvedTitle,
      };

      let results: ICopyResult[] = [];

      switch (copyOption.copyOption) {
        case CopyOption.Users: {
          const personalWorkspace: IWorkspace | undefined = workspaces.find(
            (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
          );
          results = await Promise.all(
            copyOption.selectedIds.map(async (userId) => {
              const created = await createConversation({
                conversation: {
                  ...baseConversation,
                  workspaceId:
                    personalWorkspace?.id ?? ConstantValues.EMPTY_STRING,
                  userId,
                  displayName:
                    users.find((u) => u.id === userId)?.displayName ??
                    ConstantValues.EMPTY_STRING,
                  copiedFromUser: copyOption.copiedFromUser,
                  copiedFromConversationId: copyOption.copiedFromConversationId,
                },
              });
              trackConversationCopy({ conversationId: created.id }).catch(
                console.log,
              );
              return {
                recipientId: userId,
                conversationId: created.id,
                workspaceId:
                  personalWorkspace?.id ?? ConstantValues.EMPTY_STRING,
              };
            }),
          );
          break;
        }
        case CopyOption.Workspace:
        case CopyOption.NewWorkspace: {
          results = await Promise.all(
            copyOption.selectedIds.map(async (wsId) => {
              const created = await createConversation({
                conversation: {
                  ...baseConversation,
                  workspaceId: wsId,
                  copiedFromUser: copyOption.copiedFromUser,
                  copiedFromConversationId: copyOption.copiedFromConversationId,
                },
              });
              trackConversationCopy({ conversationId: created.id }).catch(
                console.log,
              );
              return {
                recipientId: wsId,
                conversationId: created.id,
                workspaceId: wsId,
              };
            }),
          );
          break;
        }
        case CopyOption.PersonalWorkspace: {
          const personalWorkspace: IWorkspace | undefined = workspaces.find(
            (w) => w.name === ConstantValues.PERSONAL_WORKSPACE,
          );
          const created = await createConversation({
            conversation: {
              ...baseConversation,
              workspaceId: personalWorkspace?.id ?? ConstantValues.EMPTY_STRING,
              userId: currentUser.id,
              displayName:
                currentUser.displayName ?? ConstantValues.EMPTY_STRING,
              copiedFromUser: copyOption.copiedFromUser,
              copiedFromConversationId: copyOption.copiedFromConversationId,
            },
          });
          trackConversationCopy({ conversationId: created.id }).catch(
            console.log,
          );
          break;
        }
        default:
          console.warn("Unsupported copy option:", copyOption.copyOption);
      }

      queryClient.invalidateQueries(["conversations"]);
      return results;
    } catch (error) {
      console.error("Failed to share chat:", error);
      dispatch(showToast("Failed to share chat", "error"));
      return [];
    }
  };

  const loadConversationData = async (conversation: IConversation) => {
    dispatch(setCurrentConversation(conversation));
    dispatch(setCurrentMessages([]));

    const convId: string = conversation.id ?? ConstantValues.EMPTY_STRING;
    try {
      const conversationMessages = await getMessagesOfConversation({
        conversationId: convId,
      });
      await trackConversationClick({ conversationId: convId });
      if (
        !Array.isArray(conversationMessages) ||
        conversationMessages.length === 0
      )
        return;

      const lastMessage = conversationMessages[conversationMessages.length - 1];
      const lastMessageSourceRagCategories =
        lastMessage?.llmConfig?.sourceRagCategories;

      dispatch(
        setNewConfigForKey({
          key: ConstantValues.CHAT,
          config: {
            llmType: currentLLMConfig.llmType ?? LLMType.CLAUDE,
            citate: false,
            sourceRagCategories: lastMessageSourceRagCategories,
            rag: isNullUndefinedOrEmpty(lastMessageSourceRagCategories)
              ? false
              : lastMessageSourceRagCategories.length > 0,
          },
        }),
      );

      dispatch(setCurrentMessages(conversationMessages));
      dispatch(
        setTagsForKey({
          key: convId,
          tags: lastMessage?.tags || [],
        }),
      );
      dispatch(
        setLibraryDocumentsForKey({
          key: convId,
          documents: lastMessage?.selectedLibraryDocuments || [],
        }),
      );
      localStorage.setItem(
        ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
        convId,
      );
    } catch (error) {
      console.error("Failed to load chat data:", error);
      dispatch(setCurrentConversation(null));
      dispatch(setCurrentMessages([]));
    }
  };

  const handleWorkspaceSelect = async (workspace: IWorkspace) => {
    dispatch(setActiveWorkspace(workspace));
    localStorage.setItem(
      ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
      workspace.id,
    );
    setWorkspacePopoverOpen(false);

    resetChatState();
    try {
      await queryClient.invalidateQueries(["conversations"]);
    } catch (error) {
      console.error("Failed to refresh conversations:", error);
    }
  };

  const handleConversationSelect = async (conversation: IConversation) => {
    if (currentConversation?.id === conversation.id) {
      setConversationPopoverOpen(false);
      return;
    }

    dispatch(setCurrentPrompt({ key: ConstantValues.CHAT, prompt: null }));
    dispatch(clearReferencedConversations());
    setConversationPopoverOpen(false);

    await loadConversationData(conversation);
  };

  const clearLastPage = () => {
    if (!currentUser?.id) return;
    sessionStorage.removeItem(`lastPage:${currentUser.id}`);
  };

  const handleWorkspacesClick = (): void => {
    resetChatState();
    navigate("/workspaces");
    clearLastPage();
  };

  const handleConversationsClick = (): void => {
    resetChatState();
    if (activeWorkspace?.id) {
      localStorage.setItem(
        ConstantValues.MY_CHATS_FILTER_WORKSPACE_ID,
        activeWorkspace.id,
      );
    } else {
      localStorage.removeItem(ConstantValues.MY_CHATS_FILTER_WORKSPACE_ID);
    }
    navigate("/chats");
    clearLastPage();
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.leftSection}>
          <Popover
            open={workspacePopoverOpen}
            onOpenChange={(_, data) => {
              if (isGenerating || isLoadingWorkspaces) return;
              setWorkspacePopoverOpen(data.open);
              if (data.open) setConversationPopoverOpen(false);
            }}
            positioning="below-start"
          >
            <PopoverTrigger disableButtonEnhancement>
              <div
                role="button"
                tabIndex={isGenerating || isLoadingWorkspaces ? -1 : 0}
                aria-haspopup="listbox"
                aria-expanded={workspacePopoverOpen}
                aria-disabled={isGenerating || isLoadingWorkspaces}
                className={mergeClasses(
                  styles.dropdownTrigger,
                  (isGenerating || isLoadingWorkspaces) &&
                    styles.dropdownTriggerDisabled,
                )}
                onClick={() =>
                  !(isGenerating || isLoadingWorkspaces) &&
                  setWorkspacePopoverOpen(true)
                }
              >
                <OverflowTooltip
                  content={activeWorkspace?.name ?? SELECT_WORKSPACE}
                  className={styles.truncatedTextWrapper}
                >
                  <Text className={styles.dropdownLabel}>
                    {activeWorkspace?.name ?? SELECT_WORKSPACE}
                  </Text>
                </OverflowTooltip>
                <ChevronUpDown16Regular className={styles.chevronIcon} />
              </div>
            </PopoverTrigger>
            <PopoverSurface className={styles.popoverContent}>
              <div className={styles.popoverHeader}>
                <Text className={styles.popoverHeaderTitle}>Workspaces</Text>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Open16Regular />}
                  aria-label="Open in new tab"
                  iconPosition="after"
                  onClick={handleWorkspacesClick}
                  className={styles.popoverActionButton}
                >
                  View all
                </Button>
              </div>
              <div className={styles.popoverList} role="listbox">
                {sortedWorkspaces.map((workspace) => (
                  <div
                    key={workspace.id}
                    role="option"
                    tabIndex={0}
                    aria-selected={workspace.id === activeWorkspace?.id}
                    className={mergeClasses(
                      styles.popoverItem,
                      workspace.id === activeWorkspace?.id &&
                        styles.popoverItemSelected,
                    )}
                    onClick={() => handleWorkspaceSelect(workspace)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleWorkspaceSelect(workspace);
                      }
                    }}
                  >
                    <WorkspaceIcon
                      size={16}
                      className={styles.popoverItemIcon}
                    />
                    <OverflowTooltip
                      content={workspace.name}
                      className={styles.popoverItemTextWrapper}
                    >
                      <Text className={styles.popoverItemText}>
                        {workspace.name}
                      </Text>
                    </OverflowTooltip>
                    {workspace.id === activeWorkspace?.id && (
                      <Checkmark16Regular className={styles.checkIcon} />
                    )}
                  </div>
                ))}
              </div>
            </PopoverSurface>
          </Popover>

          <Text className={styles.separator}>/</Text>

          <Popover
            open={conversationPopoverOpen}
            onOpenChange={(_, data) => {
              if (isGenerating) return;
              setConversationPopoverOpen(data.open);
              if (data.open) setWorkspacePopoverOpen(false);
            }}
            positioning="below-start"
          >
            <PopoverTrigger disableButtonEnhancement>
              <div
                role="button"
                tabIndex={isGenerating ? -1 : 0}
                aria-haspopup="listbox"
                aria-expanded={conversationPopoverOpen}
                aria-disabled={isGenerating || isLoadingConversations}
                className={mergeClasses(
                  styles.dropdownTrigger,
                  isGenerating && styles.dropdownTriggerDisabled,
                )}
              >
                <OverflowTooltip
                  content={displayTitle}
                  className={styles.truncatedTextWrapper}
                >
                  <Text className={styles.dropdownLabel}>{displayTitle}</Text>
                </OverflowTooltip>
                <ChevronUpDown16Regular className={styles.chevronIcon} />
              </div>
            </PopoverTrigger>
            <PopoverSurface className={styles.popoverContent}>
              <div className={styles.popoverHeader}>
                <Text className={styles.popoverHeaderTitle}>Recent chats</Text>
                {!isLoadingConversations && conversations?.length > 0 ? (
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Open16Regular />}
                    aria-label="Open in new tab"
                    iconPosition="after"
                    onClick={handleConversationsClick}
                    className={styles.popoverActionButton}
                  >
                    View all
                  </Button>
                ) : (
                  <></>
                )}
              </div>
              <div className={styles.popoverList} role="listbox">
                {isLoadingConversations ? (
                  <div className={styles.popoverItemCenter}>
                    <Spinner size="tiny" />
                  </div>
                ) : conversations?.length == 0 ? (
                  <div className={styles.popoverItem}>No chats yet</div>
                ) : (
                  conversations.map((conversation: IConversation) => (
                    <div
                      key={conversation.id}
                      role="option"
                      tabIndex={0}
                      aria-selected={
                        conversation.id === currentConversation?.id
                      }
                      className={mergeClasses(
                        styles.popoverItem,
                        conversation.id === currentConversation?.id &&
                          styles.popoverItemSelected,
                      )}
                      onClick={() => handleConversationSelect(conversation)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleConversationSelect(conversation);
                        }
                      }}
                    >
                      <ConversationIcon
                        size={16}
                        className={styles.popoverItemIcon}
                      />
                      <OverflowTooltip
                        content={conversation.title}
                        className={styles.popoverItemTextWrapper}
                      >
                        <Text className={styles.popoverItemText}>
                          {conversation.title}
                        </Text>
                      </OverflowTooltip>
                      {conversation.id === currentConversation?.id && (
                        <Checkmark16Regular className={styles.checkIcon} />
                      )}
                    </div>
                  ))
                )}
              </div>
            </PopoverSurface>
          </Popover>

          {currentConversation?.id && (
            <Tooltip
              content={
                isGenerating
                  ? "Wait for response to complete"
                  : "Start a new chat"
              }
              relationship="label"
              showDelay={300}
              hideDelay={100}
            >
              <Button
                className={mergeClasses(
                  sharedButtonStyles.primaryButton,
                  styles.newChatButtonSize,
                )}
                icon={<Add16Regular />}
                disabled={isGenerating}
                onClick={() => {
                  resetChatState();
                  navigate(`/?workspaceId=${encodeURIComponent(workspaceId)}`);
                }}
                aria-label="Start a new chat"
              />
            </Tooltip>
          )}

          {currentConversation && (
            <Tooltip
              content={
                isGenerating
                  ? "Wait for response to complete"
                  : "Share this chat"
              }
              relationship="label"
              showDelay={300}
              hideDelay={100}
            >
              <Button
                ref={shareButtonRef}
                appearance="subtle"
                className={mergeClasses(
                  sharedButtonStyles.secondaryButton,
                  styles.shareButtonSize,
                )}
                icon={<Share16Regular />}
                disabled={isGenerating}
                onClick={() => setIsShareDialogOpen(true)}
                aria-label="Share this chat"
              />
            </Tooltip>
          )}
        </div>

        <div className={styles.rightSection}>
          <Tooltip
            content="Add knowledge components, documents, and past chats as context sources for this chat."
            relationship="label"
          >
            <Button
              appearance="primary"
              icon={<DocumentCopyIcon size={20} />}
              onClick={onManageContentClick}
              className={styles.manageContentButton}
            >
              <span className={styles.manageContentButtonText}>
                Manage Content
              </span>
              {contentCount > 0 && (
                <span className={styles.badge}>{contentCount}</span>
              )}
            </Button>
          </Tooltip>
        </div>
      </div>

      {isShareDialogOpen && currentConversation && (
        <CopyOptionsDialog
          conversation={currentConversation}
          isOpen={isShareDialogOpen}
          onClose={() => {
            setIsShareDialogOpen(false);
            shareButtonRef.current?.focus();
          }}
          onCopy={(options) => {
            setIsShareDialogOpen(false);
            shareButtonRef.current?.focus();
            if (options) {
              return handleCopy(currentConversation, options);
            }
          }}
        />
      )}
    </>
  );
};
